import type { JSTProctorBridge } from './JSTProctorBridge';
import type { CanonicalEnvironment } from '../../environment';
import type {
    NavBarBackgroundColor,
    TopNavTitleBlock,
    NavBarItemWithCallbackId,
} from '../tare/NavBarArgs';
import type { LogMetadata } from '@indeed/signals-logger';

export type ElementType = 'ALERT' | 'BANNER' | 'TOAST' | 'SUB_TAB';

export type ShareSheetAction = 'sharedAction' | 'dismissedAction';
export interface ShareSheetResult {
    action: ShareSheetAction;
}

export interface TareAppModel {
    appVersion: string;
    buildConfig: string;
    buildEnvironment: CanonicalEnvironment;
    locale: {
        languageCode: string;
        countryCode: string;
    };
}

export interface TareAccountStatus {
    isLoggedIn: boolean;
    id: string | null;
    accountId: string | null;
    emailAddress: string | null;
    isConfirmed: boolean;
    creationDate: number | null;
}

export enum JSTOrientation {
    PORTRAIT = 'PORTRAIT',
    LANDSCAPE = 'LANDSCAPE',
    UNDEFINED = 'UNDEFINED',
}

export interface TareDeviceModel {
    ctk: string | null;
    deviceId: string | null;
    manufacturer: string;
    manufacturerModel: string;
    orientation: JSTOrientation;
    osVersion: string;
}

export interface JSTAppSessionInfo {
    appSessionId: string;
    sessionId: string;
    installId: string;
    launchCount: number;
    sessionCount: number;
    firstInstallVersion: string | null;
    upgradeCount: number;
}

export type ProfilePreferenceType =
    | 'PREFERRED_JOB_TITLES'
    | 'MINIMUM_BASE_PAY'
    | 'RELOCATION'
    | 'REMOTE';

export type ProfilePreferenceEditResult = 'COMPLETED' | 'DELETED' | 'CANCELLED' | 'ERROR';

export type OpenLoginModalResult = 'SUCCESS' | 'CANCEL' | 'ERROR';

interface PreferredJobTitlesPreferenceData {
    jobTitles?: string[] | null;
}

interface ProfilePreferenceMinimumBasePayData {
    amount?: number | null;
    currency?: string | null;
    payPeriod?: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'YEARLY' | null;
}

interface ProfilePreferenceRelocationData {
    locations?: string[] | null;
    option?:
        | 'NOT_WILLING_TO_RELOCATE'
        | 'WILLING_TO_RELOCATE_ANYWHERE'
        | 'WILLING_TO_RELOCATE_TO_SPECIFIC_LOCATIONS'
        | null;
}

interface ProfilePreferenceRemoteData {
    selected?: ('REMOTE' | 'HYBRID_WORK' | 'IN_PERSON' | 'TEMPORARILY_REMOTE')[] | null;
}

export type ProfilePreferenceFormData =
    | { type: 'PREFERRED_JOB_TITLES'; data?: PreferredJobTitlesPreferenceData | null }
    | {
          type: 'MINIMUM_BASE_PAY';
          data?: ProfilePreferenceMinimumBasePayData | null;
      }
    | {
          type: 'RELOCATION';
          data?: ProfilePreferenceRelocationData | null;
      }
    | {
          type: 'REMOTE';
          data?: ProfilePreferenceRemoteData | null;
      };
// individual interfaces for local preferences methods
export interface GetLocalPrefsBoolean {
    getBoolean(namespace: string, key: string, defaultValue?: boolean): Promise<boolean>;
}

export interface SetLocalPrefsBoolean {
    setBoolean(namespace: string, key: string, newValue: boolean): Promise<void>;
}

export interface GetLocalPrefsInt {
    getInt(namespace: string, key: string, defaultValue?: number): Promise<number>;
}

export interface SetLocalPrefsInt {
    setInt(namespace: string, key: string, newValue: number): Promise<void>;
}

export interface GetLocalPrefsLong {
    getLong(namespace: string, key: string, defaultValue?: number): Promise<number>;
}

export interface SetLocalPrefsLong {
    setLong(namespace: string, key: string, newValue: number): Promise<void>;
}

export interface GetLocalPrefsString {
    getString(namespace: string, key: string, defaultValue?: string): Promise<string | null>;
}

export interface SetLocalPrefsString {
    setString(namespace: string, key: string, newValue: string): Promise<void>;
}

export interface ClearLocalPrefsNamespace {
    clearNamespace(namespace: string): Promise<void>;
}

// Define TareLocalPrefs as a type alias instead of an interface with extends
export type TareLocalPrefs = GetLocalPrefsBoolean &
    SetLocalPrefsBoolean &
    GetLocalPrefsInt &
    SetLocalPrefsInt &
    GetLocalPrefsLong &
    SetLocalPrefsLong &
    GetLocalPrefsString &
    SetLocalPrefsString &
    ClearLocalPrefsNamespace;

export type JSTPaywallDisplayStyle = 'PAYMENT' | 'AI_INTERVIEW';

export interface JSTPaywallResult {
    paywallScreenResult: 'SUCCESS' | 'USER_CANCELLED' | 'PENDING' | 'UNKNOWN' | 'FAILURE';
    productId?: string;
}

export interface GetCachedViewJobHint {
    getCachedViewJobHint(key: string): Promise<ViewJobHint | null>;
}

export interface SetCachedViewJobHint {
    setCachedViewJobHint(key: string, hint: ViewJobHint): Promise<void>;
}

export interface ViewJobHint {
    isSaved: boolean | null;
    title: string | null;
    isFromAce: boolean | null;
    jobResultTrackingKey: string | null;
    isDisliked: boolean | null;
    matchFactor: string | null;
}

export interface GetCachedViewJobFeed {
    getCachedViewJobFeed(homepageTk: string): Promise<ViewJobFeed | null>;
}

export interface SetCachedViewJobFeed {
    setCachedViewJobFeed(homepageTk: string, viewJobFeed: ViewJobFeed): Promise<void>;
}

export interface UpdateCachedViewJobFeed {
    updateCachedViewJobFeed(
        homepageTk: string,
        key: keyof ViewJobFeed,
        value: string,
    ): Promise<void>;
}

export interface ViewJobFeed {
    homepageTk: string;
    jobFeedData: string;
    lastViewedJobSection: string;
    lastViewedJobKey: string;
}

export type TareCache = GetCachedViewJobHint &
    SetCachedViewJobHint &
    GetCachedViewJobFeed &
    SetCachedViewJobFeed &
    UpdateCachedViewJobFeed;

export type ResumeMainSectionType = 'WorkExperience' | 'Education' | 'Certification';

export type ResumeOtherSectionType =
    | 'Summary'
    | 'AdditionalInfo'
    | 'Skill'
    | 'Skills'
    | 'Language'
    | 'PersonalInfo';

type ResumeFormMode =
    | { mode: 'Add' }
    | { mode: 'Edit'; itemId: string }
    | { mode: 'BuildSuggestion' };

export type ResumeFormResult =
    | 'COMPLETED'
    | 'RESUME_ITEM_BUILT'
    | 'DELETED'
    | 'CANCELLED'
    | 'ERROR';

export interface ResumeSkillFormData {
    text: string;
    experience?: number | null;
    taxonomyConceptTitle?: ResumeTaxonomyConceptTitle | null;
}

export type ProfileMonth =
    | 'JANUARY'
    | 'FEBRUARY'
    | 'MARCH'
    | 'APRIL'
    | 'MAY'
    | 'JUNE'
    | 'JULY'
    | 'AUGUST'
    | 'SEPTEMBER'
    | 'OCTOBER'
    | 'NOVEMBER'
    | 'DECEMBER';

export interface ResumeTaxonomyConceptTitle {
    label: string;
    suid: string;
    type?: string | null;
    sectionIds?: string[] | null;
    context?: string[] | null;
}

export interface ResumeSkillsFormData {
    skills: ReadonlyArray<ResumeSkillFormData>;
}

export interface ContactInfoFormData {
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    location?: string | null;
    country?: string | null;
}

type ResumeOtherSectionDataMap = {
    Summary: ProfileSummaryData;
    Skill: ResumeSkillFormData;
    Skills: ResumeSkillsFormData;
    AdditionalInfo: { additionalInfo: string };
    Language: { languages: string[] };
    PersonalInfo: ContactInfoFormData;
};

export type ResumeOtherSectionFormData<T extends ResumeOtherSectionType> = {
    type: T;
    data?: ResumeOtherSectionDataMap[T];
};

export type ResumeOtherSectionFormInputData<T extends ResumeOtherSectionType> =
    ResumeOtherSectionFormData<T> & ResumeFormMode;

// Response data type with flattened structure: { type: T, ...formFields }
export type ResumeOtherSectionResponseData<T extends ResumeOtherSectionType> = {
    type: T;
} & ResumeOtherSectionDataMap[T];

export interface ProfileDate {
    month?: ProfileMonth | null;
    year?: number | null;
}
export interface ProfileDateRange {
    fromDate?: ProfileDate | null;
    isCurrent?: boolean | null;
    toDate?: ProfileDate | null;
}

interface ProfileSummaryData {
    summary: string;
}

export interface ProfileWorkExperienceData {
    jobTitle?: string | null;
    company?: string | null;
    country?: string | null;
    location?: string | null;
    dateRange?: ProfileDateRange | null;
    description?: string | null;
}

export interface ProfileEducationData {
    levelOfEducation?: string | null;
    fieldOfStudy?: string | null;
    school?: string | null;
    country?: string | null;
    schoolLocation?: string | null;
    dateRange?: ProfileDateRange | null;
}

export interface ProfileCertificationData {
    certificationName?: string | null;
    dateRange?: ProfileDateRange | null;
    description?: string | null;
}

type ResumeMainSectionDataMap = {
    WorkExperience: ProfileWorkExperienceData;
    Education: ProfileEducationData;
    Certification: ProfileCertificationData;
    // Add other mappings
};

export type ResumeMainSectionFormData<T extends ResumeMainSectionType> = {
    type: T;
    data?: ResumeMainSectionDataMap[T];
};

export type ResumeMainSectionFormInputData<T extends ResumeMainSectionType> =
    ResumeMainSectionFormData<T> & ResumeFormMode;

export type JSTLogMetadata = LogMetadata;

// Response data type with flattened structure: { type: T, ...formFields }
export type ResumeMainSectionResponseData<T extends ResumeMainSectionType> = {
    type: T;
} & ResumeMainSectionDataMap[T];
/**
 * The TareBridge is a Native Module in React Native that provides access to the
 * native TARE APIs on Android and iOS
 *
 * It is expected to always be possible that the React Native client implementation is
 * more recent (through codepush) than the native implementation. As such we consider
 * every interface as potentially undefined and export the interface as a Partial.
 *
 * All communication across native modules happens over promises to avoid blocking.
 *
 * @IndeedReactNativeRNBridgeModuleEventEmitter
 */
export type TareBridge = Partial<
    {
        getAppModel(): Promise<TareAppModel>;
        getAppModelSync(): TareAppModel | null;
        getAccountStatus(): Promise<TareAccountStatus>;
        getAccountStatusSync(): TareAccountStatus | null;
        getDeviceModel(): Promise<TareDeviceModel>;
        getDeviceModelSync(): TareDeviceModel | null;
        getAppSessionInfo(): Promise<JSTAppSessionInfo | null>;
        getAppSessionInfoSync(): JSTAppSessionInfo | null;

        // Logging
        logTapButton(screenName: string, elementName: string): Promise<void>;
        logTapButtonWithExtraParams(
            screenName: string,
            elementName: string,
            tags: string[],
            extraParams: Record<string, string | number | boolean>,
        ): Promise<void>;
        logScreenView(screenName: string): Promise<void>;
        logElementView(
            screenName: string,
            elementName: string,
            elementType: ElementType,
            tags: string[],
            extraParams: Record<string, string | number | boolean>,
        ): Promise<void>;
        logScreenViewWithExtraParams(
            screenName: string,
            tags: string[],
            extraParams: Record<string, string | number | boolean>,
        ): Promise<void>;
        /**
         * @deprecated This is a legacy method that will be replaced with a different implementation.
         * Use this only for logging generic events that don't fit the tapButton/screenView/elementView patterns.
         */
        logLegacyGenericEvent(
            eventName: string,
            eventType: string,
            tags: string[],
            extraParams: Record<string, string | number | boolean>,
        ): Promise<void>;
        logNonFatal(
            message: string,
            errCode: number,
            errDomain: string,
            otherUserInfo: Record<string, string>,
        ): Promise<void>;
        logRNBridgeInternal(event: object, metadata?: JSTLogMetadata): Promise<void>;

        // Navigation
        openShareSheet(
            url: string,
            message: string,
            subject: string | null | undefined,
        ): Promise<ShareSheetResult>;
        copyToClipboard(text: string): Promise<void>;
        openLoginModal(
            isRegistration: boolean,
            from: string | undefined,
        ): Promise<OpenLoginModalResult>;
        setNavBarItems(
            rootTag: number,
            leading: NavBarItemWithCallbackId[],
            trailing: NavBarItemWithCallbackId[],
            title: TopNavTitleBlock,
            backgroundColor?: NavBarBackgroundColor,
            animated?: boolean,
            screenName?: string,
        ): Promise<void>;
        presentProfilePreferences(
            profilePreferenceFormData: ProfilePreferenceFormData,
            presenter: string,
        ): Promise<
            { result: ProfilePreferenceEditResult; data: ProfilePreferenceFormData } | undefined
        >;
        openPaywallModal(
            style: JSTPaywallDisplayStyle,
            from?: string,
            tk?: string,
        ): Promise<JSTPaywallResult>;

        // Local preferences
        getLocalPrefsBoolean(
            namespace: string,
            key: string,
            defaultValue?: boolean,
        ): Promise<boolean>;
        setLocalPrefsBoolean(namespace: string, key: string, newValue: boolean): Promise<void>;
        getLocalPrefsInt(namespace: string, key: string, defaultValue?: number): Promise<number>;
        setLocalPrefsInt(namespace: string, key: string, newValue: number): Promise<void>;
        getLocalPrefsLong(namespace: string, key: string, defaultValue?: number): Promise<number>;
        setLocalPrefsLong(namespace: string, key: string, newValue: number): Promise<void>;
        getLocalPrefsString(namespace: string, key: string, defaultValue?: string): Promise<string>;
        setLocalPrefsString(namespace: string, key: string, newValue: string): Promise<void>;
        clearLocalPrefsNamespace(namespace: string): Promise<void>;

        // JSTCache bridge
        getCachedViewJobHint(key: string): Promise<ViewJobHint | null>;
        setCachedViewJobHint(key: string, hint: ViewJobHint): Promise<void>;
        getCachedViewJobFeed(homepageTk: string): Promise<ViewJobFeed | null>;
        setCachedViewJobFeed(homepageTk: string, viewJobFeed: ViewJobFeed): Promise<void>;
        updateCachedViewJobFeed(
            homepageTk: string,
            key: keyof ViewJobFeed,
            value: string,
        ): Promise<void>;

        // Resume Forms
        presentResumeMainSectionForm<T extends ResumeMainSectionType>(
            resumeMainSectionFormData: ResumeMainSectionFormInputData<T>,
            presenter?: string,
        ): Promise<
            { result: ResumeFormResult; data: ResumeMainSectionResponseData<T> } | undefined
        >;
        presentResumeOtherSectionForm<T extends ResumeOtherSectionType>(
            resumeOtherSectionFormData: ResumeOtherSectionFormInputData<T>,
            presenter?: string,
        ): Promise<
            { result: ResumeFormResult; data: ResumeOtherSectionResponseData<T> } | undefined
        >;

        // SSE
        sseClient(): Promise<string>;
        sseConnect(sseClientId: string, path: string): Promise<void>;
        sseDisconnect(sseClientId: string): Promise<void>;

        /**
         * Start a streaming HTTP request executed by the native HTTP client (same stack as rest of app;
         * use for WAF/bot-detection parity). All headers (e.g. indeed-api-key, indeed-co, indeed-locale) are
         * added by native. Resolves with status and headers when response head is ready.
         * Response body is streamed via NativeFetchChunk events: { requestId, chunk? (base64), done?, error? }.
         */
        startNativeFetch(
            requestId: string,
            url: string,
            method: string,
            body: string | null,
        ): Promise<NativeFetchResponseHead>;

        /**
         * Required by NativeEventEmitter for listener subscription bookkeeping.
         */
        addListener(eventName: string): void;

        /**
         * Required by NativeEventEmitter for listener subscription bookkeeping.
         */
        removeListeners(count: number): void;
    } & JSTProctorBridge
>;

/** Response head returned when native has received HTTP status and headers. */
export interface NativeFetchResponseHead {
    status: number;
    headersJson: string;
}
